import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { checkBackendHealth } from './services/api';
import ProtectedRoute from './components/ProtectedRoute';

// Route-level code splitting: each page is its own lazily-loaded chunk, so the
// initial bundle stays small and first paint is faster.
const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Jobs = lazy(() => import('./pages/Jobs'));
const Companies = lazy(() => import('./pages/Companies'));
const CompanyDashboard = lazy(() => import('./pages/CompanyDashboard'));
const JobDetails = lazy(() => import('./pages/JobDetails'));
const MyApplications = lazy(() => import('./pages/MyApplications'));
const Interview = lazy(() => import('./pages/Interview'));
const PostJob = lazy(() => import('./pages/PostJob'));
const Report = lazy(() => import('./pages/Report'));
const CompanyProfileEdit = lazy(() => import('./pages/CompanyProfileEdit'));
const MyJobs = lazy(() => import('./pages/MyJobs'));
const CVViewer = lazy(() => import('./pages/CVViewer'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));

// Lightweight fallback shown while a route chunk loads.
const RouteFallback = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{
      width: 40, height: 40, border: '3px solid #E9ECF5', borderTopColor: '#6366F1',
      borderRadius: '50%', animation: 'appSpin 0.8s linear infinite',
    }} />
    <style>{`@keyframes appSpin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const MAX_ATTEMPTS = 5;
const RETRY_MS     = 6000;

function App() {
  const [ready,   setReady]   = useState(false);
  const [attempt, setAttempt] = useState(1);
  const [phase,   setPhase]   = useState('checking'); // 'checking' | 'waiting' | 'giveup'
  const timer  = useRef(null);
  const cancel = useRef(false);

  useEffect(() => {
    if (ready) return;
    cancel.current = false;
    setPhase('checking');

    checkBackendHealth().then(ok => {
      if (cancel.current) return;
      if (ok) {
        setReady(true);
      } else if (attempt >= MAX_ATTEMPTS) {
        setPhase('giveup');
      } else {
        setPhase('waiting');
        timer.current = setTimeout(() => {
          if (!cancel.current) setAttempt(a => a + 1);
        }, RETRY_MS);
      }
    });

    return () => {
      cancel.current = true;
      clearTimeout(timer.current);
    };
  }, [attempt, ready]);

  if (!ready) {
    const isGiveup = phase === 'giveup';
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        background: 'linear-gradient(135deg,#1c0b4b 0%,#0f172a 100%)',
        color: '#fff', fontFamily: 'system-ui,Arial,sans-serif',
        padding: '24px', textAlign: 'center',
      }}>
        <div style={{
          fontSize: 48, marginBottom: 12,
          animation: isGiveup ? 'none' : 'pulse 2s ease-in-out infinite',
        }}>🚀</div>

        <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 6px' }}>
          BotBoss
        </h1>
        <p style={{ color: '#a78bfa', fontSize: 14, margin: '0 0 36px' }}>
          AI-Powered Recruitment Platform
        </p>

        {!isGiveup ? (
          <>
            <div style={{
              width: 52, height: 52,
              border: '3px solid rgba(255,255,255,0.15)',
              borderTopColor: '#a78bfa',
              borderRadius: '50%',
              animation: 'spin 0.9s linear infinite',
              marginBottom: 24,
            }} />
            <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 8px' }}>
              {phase === 'checking' ? 'Connecting to server…' : `Retrying in a moment…`}
            </p>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', margin: '0 0 28px', maxWidth: 360 }}>
              Attempt {attempt} of {MAX_ATTEMPTS} — the free server may need ~30s to wake up
            </p>
          </>
        ) : (
          <>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚡</div>
            <p style={{ fontSize: 16, fontWeight: 600, margin: '0 0 8px' }}>
              Server is still waking up…
            </p>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', margin: '0 0 28px', maxWidth: 360 }}>
              You can enter the app now — interview features will work once the server responds.
            </p>
          </>
        )}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={() => setReady(true)} style={{
            padding: '13px 28px', borderRadius: 10, fontSize: 15, fontWeight: 700,
            background: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer',
          }}>
            {isGiveup ? 'Enter App' : 'Continue Anyway'}
          </button>
          {!isGiveup && (
            <button onClick={() => { clearTimeout(timer.current); setAttempt(a => a + 1); }}
              style={{
                padding: '13px 28px', borderRadius: 10, fontSize: 15, fontWeight: 700,
                background: 'rgba(255,255,255,0.12)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer',
              }}>
              Retry Now
            </button>
          )}
        </div>

        <style>{`
          @keyframes spin  { to { transform: rotate(360deg); } }
          @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.12)} }
        `}</style>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
        {/* Public */}
        <Route path="/"                      element={<Landing />} />
        <Route path="/login"                 element={<Login />} />
        <Route path="/signup"                element={<Signup />} />
        <Route path="/jobs"                  element={<Jobs />} />
        <Route path="/companies"             element={<Companies />} />
        <Route path="/job/:id"               element={<JobDetails />} />
        <Route path="/about"                 element={<About />} />
        <Route path="/contact"               element={<Contact />} />
        <Route path="/privacy"               element={<Privacy />} />
        <Route path="/terms"                 element={<Terms />} />

        {/* Candidate-only */}
        <Route path="/my-applications" element={<ProtectedRoute allow={['candidate']}><MyApplications /></ProtectedRoute>} />
        <Route path="/interview/:jobId" element={<ProtectedRoute allow={['candidate']}><Interview /></ProtectedRoute>} />

        {/* Any authenticated user */}
        <Route path="/report/:sessionId" element={<ProtectedRoute><Report /></ProtectedRoute>} />

        {/* Company-only */}
        <Route path="/company/dashboard" element={<ProtectedRoute allow={['company']}><CompanyDashboard /></ProtectedRoute>} />
        <Route path="/company/post-job" element={<ProtectedRoute allow={['company']}><PostJob /></ProtectedRoute>} />
        <Route path="/company/profile/edit" element={<ProtectedRoute allow={['company']}><CompanyProfileEdit /></ProtectedRoute>} />
        <Route path="/my-jobs" element={<ProtectedRoute allow={['company']}><MyJobs /></ProtectedRoute>} />
        <Route path="/company/cv-viewer" element={<ProtectedRoute allow={['company']}><CVViewer /></ProtectedRoute>} />

        <Route path="*"                      element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
